/* ==========================================================================
   AU MENU — Motion Engine  ("candlelight" motion language)
   --------------------------------------------------------------------------
   Zero dependencies. Loaded with `defer`. Safe to load on every template.

   CSS contract (implemented in aumenu-luxe.css, gated on html.js-motion):
     [data-reveal] variants: up | fade | mask | line | draw
     [data-reveal-group] > [data-reveal-child]      staggered children
     .is-revealed                                    final state
     .line-mask > .line-mask__inner                  headline line reveals
     .nav--hidden on #mainNav                        header hide-on-scroll
     .page-veil / .veil--active / .veil--lift        page transitions
     .lux-entrance on <body>                         first-visit sequence

   Everything respects prefers-reduced-motion: the boot snippet in
   theme.liquid only adds `js-motion` to <html> when motion is allowed, and
   this module re-checks before wiring anything kinetic.
   ========================================================================== */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var finePointer = window.matchMedia ? window.matchMedia('(pointer: fine)').matches : false;
  var saveData = !!(navigator.connection && navigator.connection.saveData);

  function motionAllowed() {
    return docEl.classList.contains('js-motion') && !(reduceQuery && reduceQuery.matches);
  }

  /* If the user flips reduced-motion on mid-session, degrade gracefully:
     reveal everything and drop the gating class so CSS shows final states. */
  if (reduceQuery && reduceQuery.addEventListener) {
    reduceQuery.addEventListener('change', function (e) {
      if (e.matches) {
        docEl.classList.remove('js-motion');
        revealEverythingNow();
      }
    });
  }

  function revealEverythingNow() {
    var nodes = document.querySelectorAll('[data-reveal], [data-reveal-child], .reveal');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.add('is-revealed', 'visible');
      nodes[i].style.transitionDelay = '0ms';
    }
  }

  /* ------------------------------------------------------------------
     1. Reveal engine
     Extends the legacy `.reveal → .visible` system (kept intact) with an
     attribute API. One IntersectionObserver for everything.
     ------------------------------------------------------------------ */
  function initReveals() {
    var targets = [];
    var i;

    var legacy = document.querySelectorAll('.reveal');
    for (i = 0; i < legacy.length; i++) targets.push(legacy[i]);

    var attrRoots = document.querySelectorAll('[data-reveal], [data-reveal-group]');
    for (i = 0; i < attrRoots.length; i++) targets.push(attrRoots[i]);

    if (!targets.length) return;

    if (!('IntersectionObserver' in window) || !motionAllowed()) {
      revealEverythingNow();
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var j = 0; j < entries.length; j++) {
        var entry = entries[j];
        if (!entry.isIntersecting) continue;
        var el = entry.target;
        io.unobserve(el);

        if (el.classList.contains('reveal')) el.classList.add('visible');

        var delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);

        if (el.hasAttribute('data-reveal-group')) {
          var stagger = parseInt(el.getAttribute('data-stagger') || '90', 10);
          var children = el.querySelectorAll('[data-reveal-child]');
          for (var k = 0; k < children.length; k++) {
            children[k].style.transitionDelay = (delay + k * stagger) + 'ms';
            children[k].classList.add('is-revealed');
          }
          el.classList.add('is-revealed');
        } else {
          if (delay) el.style.transitionDelay = delay + 'ms';
          el.classList.add('is-revealed');
        }

        runCountersWithin(el);
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    for (i = 0; i < targets.length; i++) io.observe(targets[i]);
  }

  /* ------------------------------------------------------------------
     2. Counters — <span data-counter data-counter-to="120"> counts up on
     reveal. Locale-aware (fr → fr-CA grouping). Reduced motion → final
     value immediately (handled because initReveals falls back).
     ------------------------------------------------------------------ */
  function formatNumber(value) {
    var lang = (docEl.classList.contains('lang-en')) ? 'en-CA' : 'fr-CA';
    try { return new Intl.NumberFormat(lang).format(value); }
    catch (e) { return String(value); }
  }

  function runCountersWithin(root) {
    var counters = root.hasAttribute && root.hasAttribute('data-counter')
      ? [root]
      : (root.querySelectorAll ? root.querySelectorAll('[data-counter]') : []);
    for (var i = 0; i < counters.length; i++) runCounter(counters[i]);
  }

  function runCounter(el) {
    if (el.__auCounted) return;
    el.__auCounted = true;
    var to = parseFloat(el.getAttribute('data-counter-to') || el.textContent || '0');
    if (isNaN(to)) return;
    if (!motionAllowed()) { el.textContent = formatNumber(to); return; }
    var duration = parseInt(el.getAttribute('data-counter-duration') || '1400', 10);
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - p, 4); /* ease-out-quart */
      el.textContent = formatNumber(Math.round(to * eased));
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
    /* rAF pauses in occluded tabs and can strand an intermediate value —
       this timeout is immune to throttling and snaps to the final number. */
    window.setTimeout(function () { el.textContent = formatNumber(to); }, duration + 500);
  }

  /* Counters that are not inside a reveal target still need to run. */
  function initOrphanCounters() {
    var counters = document.querySelectorAll('[data-counter]');
    if (!counters.length) return;
    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < counters.length; i++) runCounter(counters[i]);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      for (var j = 0; j < entries.length; j++) {
        if (entries[j].isIntersecting) { runCounter(entries[j].target); io.unobserve(entries[j].target); }
      }
    }, { threshold: 0.4 });
    for (var k = 0; k < counters.length; k++) io.observe(counters[k]);
  }

  /* ------------------------------------------------------------------
     3. Parallax — [data-parallax="0.15"]. Transform-only, rAF-driven,
     active set tracked with an IO so we never do work offscreen.
     Skipped on coarse pointers, reduced motion, and Save-Data.
     ------------------------------------------------------------------ */
  function initParallax() {
    if (!motionAllowed() || !finePointer || saveData) return;
    if (!('IntersectionObserver' in window)) return;
    var els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;

    var active = [];
    var ticking = false;

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var el = entries[i].target;
        var idx = active.indexOf(el);
        if (entries[i].isIntersecting && idx === -1) active.push(el);
        else if (!entries[i].isIntersecting && idx !== -1) active.splice(idx, 1);
      }
      if (active.length) requestTick();
    }, { rootMargin: '20% 0px 20% 0px' });

    for (var i = 0; i < els.length; i++) {
      els[i].style.willChange = 'transform';
      io.observe(els[i]);
    }

    function update() {
      ticking = false;
      var vh = window.innerHeight;
      for (var i = 0; i < active.length; i++) {
        var el = active[i];
        var rate = parseFloat(el.getAttribute('data-parallax')) || 0.15;
        var rect = el.getBoundingClientRect();
        var offset = (rect.top + rect.height / 2 - vh / 2) * -rate;
        var clamp = rect.height * 0.12;
        if (offset > clamp) offset = clamp;
        if (offset < -clamp) offset = -clamp;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      }
    }
    function requestTick() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', function () { if (active.length) requestTick(); }, { passive: true });
    window.addEventListener('resize', function () { if (active.length) requestTick(); }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------
     4. Magnetic elements — [data-magnetic]; max 5px pull, fine pointers.
     ------------------------------------------------------------------ */
  function initMagnetic() {
    if (!motionAllowed() || !finePointer) return;
    var els = document.querySelectorAll('[data-magnetic]');
    for (var i = 0; i < els.length; i++) {
      (function (el) {
        var strength = parseFloat(el.getAttribute('data-magnetic')) || 5;
        el.addEventListener('mousemove', function (e) {
          var r = el.getBoundingClientRect();
          var x = ((e.clientX - r.left) / r.width - 0.5) * 2;
          var y = ((e.clientY - r.top) / r.height - 0.5) * 2;
          el.style.transition = 'transform 200ms cubic-bezier(.16,1,.3,1)';
          el.style.transform = 'translate(' + (x * strength).toFixed(1) + 'px,' + (y * strength).toFixed(1) + 'px)';
        });
        el.addEventListener('mouseleave', function () {
          el.style.transition = 'transform 500ms cubic-bezier(.16,1,.3,1)';
          el.style.transform = 'translate(0,0)';
        });
      })(els[i]);
    }
  }

  /* ------------------------------------------------------------------
     5. Tilt — [data-tilt]; ±2.5deg, optional .tilt-glare child updated.
     ------------------------------------------------------------------ */
  function initTilt() {
    if (!motionAllowed() || !finePointer) return;
    var els = document.querySelectorAll('[data-tilt]');
    for (var i = 0; i < els.length; i++) {
      (function (el) {
        var max = parseFloat(el.getAttribute('data-tilt')) || 2.5;
        var glare = el.querySelector('.tilt-glare');
        el.style.transformStyle = 'preserve-3d';
        el.addEventListener('mousemove', function (e) {
          var r = el.getBoundingClientRect();
          var x = (e.clientX - r.left) / r.width - 0.5;
          var y = (e.clientY - r.top) / r.height - 0.5;
          el.style.transition = 'transform 150ms linear';
          el.style.transform =
            'perspective(800px) rotateX(' + (-y * max).toFixed(2) + 'deg) rotateY(' + (x * max).toFixed(2) + 'deg)';
          if (glare) {
            glare.style.opacity = '0.14';
            glare.style.transform = 'translate(' + (x * 40).toFixed(1) + '%,' + (y * 40).toFixed(1) + '%)';
          }
        });
        el.addEventListener('mouseleave', function () {
          el.style.transition = 'transform 600ms cubic-bezier(.16,1,.3,1)';
          el.style.transform = 'perspective(800px) rotateX(0) rotateY(0)';
          if (glare) glare.style.opacity = '0';
        });
      })(els[i]);
    }
  }

  /* ------------------------------------------------------------------
     6. Header choreography — hide on scroll down (past 480px), reveal on
     scroll up. Never hidden while the drawer is open or focus is inside.
     Leaves the existing `.scrolled` glass toggle (header.liquid) alone.
     ------------------------------------------------------------------ */
  function initHeader() {
    var nav = document.getElementById('mainNav');
    if (!nav) return;
    if (!motionAllowed()) return;
    var lastY = window.pageYOffset || 0;
    var ticking = false;

    function update() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var drawer = document.getElementById('navDrawer');
      var drawerOpen = drawer && drawer.classList.contains('open');
      var focusWithin = nav.contains(document.activeElement);
      var delta = y - lastY;
      if (drawerOpen || focusWithin || y < 480) {
        nav.classList.remove('nav--hidden');
      } else if (delta > 6) {
        nav.classList.add('nav--hidden');
      } else if (delta < -4) {
        nav.classList.remove('nav--hidden');
      }
      lastY = y;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    nav.addEventListener('focusin', function () { nav.classList.remove('nav--hidden'); });
  }

  /* ------------------------------------------------------------------
     7. Page veil — cinematic cross-fade between internal pages, plus the
     one-per-session homepage entrance. Must be bfcache-proof (pageshow).
     ------------------------------------------------------------------ */
  function initVeil() {
    var veil = document.querySelector('[data-page-veil]');
    if (!veil) return;

    /* Always clear the veil on show — covers bfcache restores where the
       page was cached mid-transition. Runs even without js-motion. */
    window.addEventListener('pageshow', function () {
      veil.classList.remove('veil--active');
      veil.classList.add('veil--lift');
    });

    if (!motionAllowed()) {
      veil.classList.add('veil--lift');
      return;
    }

    /* Entrance sequence: homepage, once per session. */
    var isIndex = document.body.className.indexOf('template-index') !== -1;
    var entered = null;
    try { entered = sessionStorage.getItem('au-entered'); } catch (e) {}
    if (isIndex && !entered) {
      try { sessionStorage.setItem('au-entered', '1'); } catch (e) {}
      document.body.classList.add('lux-entrance');
      veil.classList.add('veil--active');
      window.setTimeout(function () {
        veil.classList.remove('veil--active');
        veil.classList.add('veil--lift');
        window.setTimeout(function () { document.body.classList.remove('lux-entrance'); }, 900);
      }, 1050);
    } else {
      veil.classList.add('veil--lift');
    }

    /* Outbound transitions. */
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      if (a.hasAttribute('data-no-veil') || a.hasAttribute('download')) return;
      if (a.target && a.target !== '_self') return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;
      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.hash) return;
      /* Never intercept checkout / cart mutations / account. */
      if (/^\/(checkout|cart\/(add|change|update|clear)|account\/login|account\/logout)/.test(url.pathname)) return;

      e.preventDefault();
      veil.classList.remove('veil--lift');
      veil.classList.add('veil--active');
      window.setTimeout(function () { window.location.href = a.href; }, 230);
    });
  }

  /* ------------------------------------------------------------------
     8. Language sync — the header owns the FR/EN toggle. Here we watch the
     <html> class list and mirror the choice onto the `lang` attribute and
     any element carrying data-aria-fr / data-aria-en, keeping assistive
     tech in the same language as the visible copy.
     ------------------------------------------------------------------ */
  function initLangSync() {
    if (!('MutationObserver' in window)) return;
    function apply() {
      var lang = docEl.classList.contains('lang-en') ? 'en' : 'fr';
      docEl.setAttribute('lang', lang);
      var nodes = document.querySelectorAll('[data-aria-fr][data-aria-en]');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].setAttribute('aria-label', lang === 'en'
          ? nodes[i].getAttribute('data-aria-en')
          : nodes[i].getAttribute('data-aria-fr'));
      }
      var titles = document.querySelectorAll('[data-title-fr][data-title-en]');
      for (var j = 0; j < titles.length; j++) {
        titles[j].setAttribute('title', lang === 'en'
          ? titles[j].getAttribute('data-title-en')
          : titles[j].getAttribute('data-title-fr'));
      }
    }
    new MutationObserver(apply).observe(docEl, { attributes: true, attributeFilter: ['class'] });
    apply();
  }

  /* ------------------------------------------------------------------
     9. Marquee — duplicate the track for a seamless loop.
        <div class="lux-marquee"><div class="lux-marquee__track" data-marquee-clone>…</div></div>
     ------------------------------------------------------------------ */
  function initMarquee() {
    var tracks = document.querySelectorAll('[data-marquee-clone]');
    for (var i = 0; i < tracks.length; i++) {
      var clone = tracks[i].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-marquee-clone');
      tracks[i].parentNode.appendChild(clone);
    }
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function boot() {
    initVeil();
    initReveals();
    initOrphanCounters();
    initParallax();
    initMagnetic();
    initTilt();
    initHeader();
    initLangSync();
    initMarquee();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
